// @flow

import * as React from 'react';
import { Box, useSystem } from 'react-system';
import { Chip } from '@material-ui/core';
import { graphql, useFragment } from 'react-relay';
import { useLocale } from '../../../hooks/locale';
import { createDescriptor } from '../../../controls/router';
import { useTheme } from '../../../theme';
import * as icons from '../../../icons';
import { PlaceInput } from '../../place-input.js';

// eslint-disable-next-line no-unused-vars -- Indirectly used by TopFilters_page fragment
import { CountrySwitcherAdornment } from '../../country-switcher.js';
import {
  sortDescriptor,
  pageDescriptor,
  mapBoundsDescriptor,
  placesDescriptor,
} from './sharedDescriptors';
import type { TopFilters_root$key } from './__generated__/TopFilters_root.graphql';
import type { TopFiltersCustomPage_root$key } from './__generated__/TopFiltersCustomPage_root.graphql';
import type { TopFilters_page$key } from './__generated__/TopFilters_page.graphql.js';
import type { TopFiltersCustomPage_page$key } from './__generated__/TopFiltersCustomPage_page.graphql.js';

const combinedDescriptor = createDescriptor(
  placesDescriptor,
  mapBoundsDescriptor,
  pageDescriptor,
);

const usePlacesNameStore = (paramsPlaces, apiPlaces) => {
  const localNamesCache = React.useRef({});
  return {
    addLocal(place) {
      localNamesCache.current[place.slug] = place.label;
    },
    placeSlugs: paramsPlaces.map(p => ({
      slug: p.slug,
      lang: p.lang,
      label:
        localNamesCache.current[p.slug] ??
        apiPlaces?.find(ap => ap.slug === p.slug)?.label ??
        p.slug,
    })),
  };
};

const TopFiltersView = ({ root }) => {
  const { t } = useLocale();
  const { media } = useSystem();
  const [params, setParams, flushParams] = combinedDescriptor.useState();
  const [sortParams, setSortParams] = sortDescriptor.useState();
  const [inputValue, setInputValue] = React.useState('');
  const { colors } = useTheme();
  const autosuggestPlaceholderRef = React.useRef(null);

  // We use params.places as the source of truth,
  // because it updates faster than root.aggregatesListings.places.
  // Hovewer, params.places doesn't have places' names,
  // so we need this trick to make it all work.

  const placesStore = usePlacesNameStore(
    params.placeSlugs,
    root.aggregatesListings
      ? root.aggregatesListings?.filters.places ?? []
      : root.aggregatorCustomPageBySlug
      ? root.aggregatorCustomPageBySlug?.filters.places
      : [],
  );

  const flush = () => {
    setParams({ page: 0 });
    flushParams();
  };

  const removePlace = place => {
    const newPlaces = params.placeSlugs.filter(p => p.slug !== place.slug);

    setParams({ placeSlugs: newPlaces });

    if (newPlaces.length !== 0) {
      // we don't reset map filter if places are still exists
      setParams(mapBoundsDescriptor.defaults);
    }

    if (newPlaces.length === 0 && sortParams.sortBy === 'distance') {
      setSortParams({
        sortBy: null,
        sortDirection: null,
      });
    }

    flush();
  };

  const chipClassName = 'top-filters-chip';
  const moreChipClassName = 'top-more-filters-chip';

  // chips per system breakpoint
  const maxChipsInLine = [5, 0, 3, 5];
  const placeChipsCount = placesStore.placeSlugs.length;

  const getHiddenChipsCount = (chips, maxChips) => {
    // special case
    if (maxChips === 0) {
      return placeChipsCount;
    }
    // show only 2+ and higher
    if (placeChipsCount - maxChips < 1) {
      return 0;
    }
    // one is reserved for "more" chip
    return placeChipsCount - maxChips + 1;
  };

  const moreChips = maxChipsInLine.map((maxChips, currentIndex) => {
    const hiddenChipsCount = getHiddenChipsCount(placeChipsCount, maxChips);
    if (hiddenChipsCount < 1) {
      return null;
    }
    const display = maxChipsInLine.map((_, index) => {
      if (index === currentIndex) {
        return 'block';
      } else {
        return 'none';
      }
    });
    return (
      <Box
        key={currentIndex}
        css={media({ display })}
        className={moreChipClassName}
        pr={2}
        my={'4px'}
        flexShrink={0}
      >
        <Chip
          css={{
            fontSize: '1rem',
            fontWeight: '600',
            maxWidth: '100%',
            backgroundColor: colors.blue50,
            '.MuiChip-label': {
              display: 'block',
              whiteSpace: 'nowrap',
            },
          }}
          label={t('morePlaces', { count: hiddenChipsCount })}
        />
      </Box>
    );
  });

  const placeChips = placesStore.placeSlugs.map((place, currentIndex) => {
    const display = maxChipsInLine.map(maxChips => {
      // show only 2+ and higher
      const hiddenChipsCount = getHiddenChipsCount(placeChipsCount, maxChips);
      if (currentIndex < hiddenChipsCount) {
        return 'none';
      } else {
        return 'block';
      }
    });
    return (
      <Box
        className={chipClassName}
        css={media({ display })}
        key={place.slug}
        pr={2}
        my={'4px'}
      >
        <Chip
          css={{
            fontSize: '1rem',
            fontWeight: '600',
            maxWidth: '100%',
            '.MuiChip-label': {
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          label={place.label}
          deleteIcon={
            <Box
              p={1}
              ml={0}
              flexShrink={0}
              css={{
                borderRadius: '50%',
                backgroundColor: colors.grey100,
                cursor: 'pointer',
                width: 'unset',
                height: 'unset',
              }}
              // seems like a bug of react-select or material-ui, onDelete not working on mobile
              // so we intercept onTouch
              onTouchEnd={e => {
                removePlace(place);
                // Prevent focus event if Input is not focused
                // do nothing instead, have issues on mobile sometimes
                // btw its just focus lost on deleting chip
                if (autosuggestPlaceholderRef.current != null) {
                  if (
                    !autosuggestPlaceholderRef.current.contains(
                      document.activeElement,
                    )
                  ) {
                    e.preventDefault();
                  }
                }
              }}
              // onMouseDown is used to prevent focus lost before delete
              // in case of focus lost delete also not called when needed
              onMouseDown={e => {
                removePlace(place);
                // Prevent focus event if Input is not focused
                // do nothing instead
                if (autosuggestPlaceholderRef.current != null) {
                  if (
                    !autosuggestPlaceholderRef.current.contains(
                      document.activeElement,
                    )
                  ) {
                    e.stopPropagation();
                  }
                }
              }}
            >
              <icons.Close />
            </Box>
          }
          onDelete={() => removePlace(place)}
        />
      </Box>
    );
  });

  return (
    <div ref={autosuggestPlaceholderRef}>
      <PlaceInput
        styles={{
          input: [
            media({
              flexWrap: ['wrap', 'nowrap'],
              marginTop: [14, 0],
            }),
            {
              paddingTop: 7,
              paddingBottom: 7,
              backgroundColor: colors.white,
              '&.Mui-focused': {
                flexWrap: 'wrap',
                [`.${chipClassName}`]: {
                  display: 'block',
                },
                [`.${moreChipClassName}`]: {
                  display: 'none',
                },
              },
              '.MuiOutlinedInput-input': {
                minWidth: 110,
                flex: 1,
                padding: '10.5px 12px',
              },
            },
          ],
          menu: {
            // overlap top filters mobile dialog
            zIndex: 1310,
          },
        }}
        placeholder={t('searchLocation')}
        startAdornment={
          <>
            {moreChips}
            {placeChips}
          </>
        }
        value={inputValue}
        onChange={value => setInputValue(value)}
        onSelect={place => {
          setInputValue('');
          if (params.placeSlugs.some(x => x.slug === place.slug)) {
            // Already added
            return;
          }
          placesStore.addLocal(place);
          setParams({
            placeSlugs: [
              ...params.placeSlugs,
              {
                slug: place.slug,
                lang: place.lang,
              },
            ],
            // reset map filters
            ...mapBoundsDescriptor.defaults,
          });
          flush();
        }}
      />
    </div>
  );
};

type Props = {|
  page: ?TopFilters_page$key,
  root: TopFilters_root$key,
|};

export const TopFilters = (props: Props): React.Node => {
  const page = useFragment(
    graphql`
      fragment TopFilters_page on CMSPage {
        ...countrySwitcher_page
      }
    `,
    props.page,
  );

  const root = useFragment(
    graphql`
      fragment TopFilters_root on Query {
        aggregatesListings(
          page: $page
          sortBy: $sortBy
          sortDirection: $sortDirection
          filters: $filters
        ) {
          filters {
            places {
              slug
              lang
              label
            }
          }
        }
      }
    `,
    props.root,
  );

  return <TopFiltersView root={root} page={page} />;
};

type CustomPageProps = {|
  page: ?TopFiltersCustomPage_page$key,
  root: TopFiltersCustomPage_root$key,
|};

export const TopFiltersCutomPage = (props: CustomPageProps): React.Node => {
  const page = useFragment(
    graphql`
      fragment TopFiltersCustomPage_page on CMSPage {
        ...countrySwitcher_page
      }
    `,
    props.page,
  );

  const root = useFragment(
    graphql`
      fragment TopFiltersCustomPage_root on Query {
        aggregatorCustomPageBySlug(slug: $slug) {
          filters {
            places {
              slug
              lang
              label
            }
          }
        }
      }
    `,
    props.root,
  );

  return <TopFiltersView root={root} page={page} />;
};
